/**
 * Static table of MSSQL/T-SQL built-in function signatures for client-side
 * signature help — mirrors the SQLite precedent (`sqliteBuiltinRoutines.ts`)
 * because SQL Tools Service's own `textDocument/signatureHelp` answers
 * `{signatures: null}` uniformly for every input (see `functionSignatureHelp.ts`'s
 * file header and Plan 20 §10 step 4's dead-path removal).
 *
 * Unlike SQLite, MSSQL ALSO has user routines resolvable via `sys.parameters`
 * (the existing `resolveRoutine` catalog path, shipped in step 4). This
 * static map covers BUILT-INS only and is consulted FIRST in
 * `computeFunctionSignatureHelp`'s mssql branch — but only for unqualified or
 * `sys.`-qualified calls. A call qualified with any OTHER schema
 * (`dbo.Foo(`, e.g. a user-created routine that happens to share a built-in's
 * name) always resolves through the `sys.parameters` catalog path instead —
 * this mirrors real SQL Server name resolution, where a recognized built-in
 * function token binds before object resolution only when the call is
 * unqualified. See the precedence comment at the mssql branch in
 * `functionSignatureHelp.ts` and its "built-ins vs user-routine precedence"
 * test block.
 *
 * Split into per-family sibling files (string / datetime / math / aggregate
 * [+ window-ranking] / system [+ hashing] / json / conversion
 * [+ conditional/null]) to keep each file well under ~200 lines; this barrel
 * merges them into one lookup map.
 *
 * Coverage: every argument-taking name in `MSSQL_BUILTIN_FUNCTION_LIST`
 * (`src/features/intellisense/resolvers/mssqlBuiltinFunctions.ts` — the
 * completion catalog users actually see) has either a catalog entry here or
 * an explicit exclusion below with a reason. Enforced by
 * `mssqlBuiltinRoutines.test.ts`'s coverage harness.
 */

import type { RoutineMetadata } from '../../../../types/electron';
import { MSSQL_STRING_ROUTINES } from './mssqlBuiltinRoutines.string';
import { MSSQL_DATETIME_ROUTINES } from './mssqlBuiltinRoutines.datetime';
import { MSSQL_MATH_ROUTINES } from './mssqlBuiltinRoutines.math';
import { MSSQL_AGGREGATE_ROUTINES } from './mssqlBuiltinRoutines.aggregate';
import { MSSQL_SYSTEM_ROUTINES } from './mssqlBuiltinRoutines.system';
import { MSSQL_JSON_ROUTINES } from './mssqlBuiltinRoutines.json';
import { MSSQL_CONVERSION_ROUTINES } from './mssqlBuiltinRoutines.conversion';

export type MssqlBuiltinEntry = RoutineMetadata & { documentation?: string };

/** Lowercase-function-name → signature metadata. Consumed synchronously (no IPC, no connection needed) by `computeFunctionSignatureHelp`. */
export const MSSQL_BUILTIN_ROUTINES: ReadonlyMap<string, MssqlBuiltinEntry> = new Map([
  ...MSSQL_STRING_ROUTINES,
  ...MSSQL_DATETIME_ROUTINES,
  ...MSSQL_MATH_ROUTINES,
  ...MSSQL_AGGREGATE_ROUTINES,
  ...MSSQL_SYSTEM_ROUTINES,
  ...MSSQL_JSON_ROUTINES,
  ...MSSQL_CONVERSION_ROUTINES,
]);

/**
 * Built-in names from `MSSQL_BUILTIN_FUNCTION_LIST` that are deliberately
 * NOT in the catalog above, each with a one-line reason (lowercase key).
 * Most are niladic keywords (no parens at all in T-SQL, e.g.
 * `CURRENT_TIMESTAMP`) or zero-argument functions with mandatory empty
 * parens (`GETDATE()`) — the latter would be filtered out anyway by
 * `computeFunctionSignatureHelp`'s `parameters.length > 0` gate (no hints to
 * show), so an explicit exclusion with a reason is clearer than a dead
 * zero-param catalog entry.
 */
export const MSSQL_EXCLUDED_FROM_SIGNATURE_HELP: ReadonlyMap<string, string> = new Map([
  ['current_timestamp', 'niladic keyword — no parens in T-SQL'],
  ['current_user', 'niladic keyword — no parens in T-SQL'],
  ['session_user', 'niladic keyword — no parens in T-SQL'],
  ['system_user', 'niladic keyword — no parens in T-SQL'],
  ['current_timezone', 'zero-arg (mandatory empty parens) — no useful hint to show'],
  ['current_timezone_id', 'zero-arg (mandatory empty parens) — no useful hint to show'],
  ['getdate', 'zero-arg — no useful hint to show'],
  ['getutcdate', 'zero-arg — no useful hint to show'],
  ['sysdatetime', 'zero-arg — no useful hint to show'],
  ['sysdatetimeoffset', 'zero-arg — no useful hint to show'],
  ['sysutcdatetime', 'zero-arg — no useful hint to show'],
  ['pi', 'zero-arg — no useful hint to show'],
  ['app_name', 'zero-arg — no useful hint to show'],
  ['host_id', 'zero-arg — no useful hint to show'],
  ['host_name', 'zero-arg — no useful hint to show'],
  ['newid', 'zero-arg — no useful hint to show'],
  ['newsequentialid', 'zero-arg — no useful hint to show'],
  ['original_login', 'zero-arg — no useful hint to show'],
  ['cume_dist', 'zero-arg — its arguments live in the trailing OVER(...) clause, not the call parens'],
  ['dense_rank', 'zero-arg — arguments live in OVER(...)'],
  ['percent_rank', 'zero-arg — arguments live in OVER(...)'],
  ['rank', 'zero-arg — arguments live in OVER(...)'],
  ['row_number', 'zero-arg — arguments live in OVER(...)'],
]);
